import { resolveOrganizationId } from '@/lib/routerUtils'
import type { OrganizationAction, OrganizationActionPayload } from '@/types/organization'
import { resolveActionTypeLabel } from '@/utils/actionTypeUtils'
import { isDemoDeployment } from '@/utils/deployment'
import type { AxiosRequestConfig } from 'axios'
import { apiCall, ownerApiCall } from './axiosInstance'
import { actionsApi as demoActionsApi } from './demo/actionsApi'
import type { DeviceSearchFilters, Filters } from './deviceApi'
import { organizationsApi } from './organizationsApi'

/**
 * Headers an org-level caller must attach to reach a tenant-scoped endpoint: the owning project
 * tenant plus admin impersonation (org owners are not direct members of sub-tenants, so the
 * tenant header alone yields no role). Mirrors createActionV3. Empty at tenant level, where the
 * URL already supplies the tenant and the axios interceptor fills the header.
 */
const tenantScopedConfig = (tenantId?: string): AxiosRequestConfig =>
	tenantId ? { headers: { 'x-bytebeam-tenant': tenantId, 'x-bytebeam-impersonate': 'admin' } } : {}

export function normalizeOrganizationActionsResponse(data: unknown): {
	actions: OrganizationAction[]
	count: number
} {
	if (data == null || typeof data !== 'object') {
		return { actions: [], count: 0 }
	}
	const o = data as Record<string, unknown>
	const countRaw = o.count
	const count = typeof countRaw === 'number' ? countRaw : Number(countRaw) || 0
	const raw = o.actions ?? o.result
	const actions = (Array.isArray(raw) ? raw : []) as OrganizationAction[]
	return { actions, count }
}

/**
 * Org-actions rows carry timestamps like `2026-07-07 06:28:32.043333 +00:00:00` — the
 * `+HH:MM:SS` offset is unparseable by dayjs/Date (renders as a bogus "a month ago").
 * Trim the offset's seconds to get a standard `+HH:MM`; standard strings pass through.
 */
export function normalizeActionTimestamp(value: string): string {
	return value.replace(/([+-]\d{2}:\d{2}):\d{2}$/, '$1')
}

function timestampOrNull(value: unknown): string | null {
	return typeof value === 'string' ? normalizeActionTimestamp(value) : null
}

function deviceIdFromFilter(deviceFilter: unknown): string | undefined {
	if (deviceFilter == null || typeof deviceFilter !== 'object') {
		return undefined
	}
	const id = (deviceFilter as { id?: { eq?: string; in?: string[] } }).id
	if (id?.eq != null) {
		return String(id.eq)
	}
	const first = id?.in?.[0]
	return first != null ? String(first) : undefined
}

function deviceStateFromActionStats(
	stats: DeviceActionStats | undefined,
	deviceId: string,
): { action_state: string; progress: number; errors: string | null } {
	let action_state = 'queued'
	let progress = 0
	let errors: string | null = null
	if (stats && typeof stats === 'object') {
		for (const [state, list] of Object.entries(stats)) {
			if (!Array.isArray(list)) {
				continue
			}
			const hit = list.find(entry => entry && String(entry.device_id) === deviceId)
			if (hit) {
				action_state = state
				progress = hit.progress ?? 0
				errors = hit.errors ?? null
				break
			}
		}
	}
	return { action_state, progress, errors }
}

/** Maps org device-actions API rows (v3 `result`) to tenant device-action table rows. */
export function mapOrganizationDeviceActionToEntry(
	raw: unknown,
	fallbackDeviceId?: string,
): DeviceActionEntryV3 | null {
	if (raw == null || typeof raw !== 'object') {
		return null
	}
	const o = raw as Record<string, unknown>
	const id = o.id ?? o.action_id
	if (id == null) {
		return null
	}

	const payload = (o.payload && typeof o.payload === 'object' ? o.payload : {}) as Record<string, unknown>
	const deviceId =
		(typeof o.device_id === 'string' ? o.device_id : undefined) ??
		(typeof payload.device_id === 'string' ? payload.device_id : undefined) ??
		deviceIdFromFilter(o.device_filter) ??
		fallbackDeviceId ??
		''

	const rawType = typeof o.type === 'string' ? o.type : ''
	const reprovisionType = typeof o.reprovision_type === 'string' ? o.reprovision_type : undefined

	let action_state = typeof o.action_state === 'string' ? o.action_state : undefined
	let progress: number | null =
		typeof o.progress === 'number' ? o.progress : o.progress == null ? null : Number(o.progress) || 0
	let errors: string | null =
		o.errors == null ? null : typeof o.errors === 'string' ? o.errors : JSON.stringify(o.errors)

	if (action_state == null) {
		const fromStats = deviceStateFromActionStats(o.device_action_stats as DeviceActionStats | undefined, deviceId)
		action_state = fromStats.action_state
		progress = fromStats.progress
		if (errors == null) {
			errors = fromStats.errors
		}
	}

	// device_id is the device's current id; reprovision rows need the id frozen at creation time
	// instead (see filter_device_id doc). Skipped for regular commands — device_id is already
	// correct there, and their device_filter isn't necessarily a device-id query.
	const filterDeviceId = reprovisionType ? deviceIdFromFilter(o.device_filter) : undefined
	const uid = typeof o.uid === 'number' || typeof o.uid === 'string' ? o.uid : o.uid == null ? undefined : String(o.uid)

	return {
		action_id: typeof id === 'number' ? id : Number(id),
		device_id: deviceId,
		filter_device_id: filterDeviceId,
		uid,
		action_state: action_state ?? 'queued',
		retry_count: typeof o.retry_count === 'number' ? o.retry_count : 0,
		timeout_at: timestampOrNull(o.timeout_at),
		updated_at: timestampOrNull(o.updated_at) ?? timestampOrNull(o.created_at),
		device_reported_state: typeof o.device_reported_state === 'string' ? o.device_reported_state : null,
		progress,
		errors,
		mqtt_publish_at: timestampOrNull(o.mqtt_publish_at),
		payload,
		type: resolveActionTypeLabel(rawType, reprovisionType),
		created_at: typeof o.created_at === 'string' ? normalizeActionTimestamp(o.created_at) : '',
		created_by: typeof o.created_by === 'string' ? o.created_by : '',
		// Action's own tenant (source tenant for reprovision/transfer) — scopes logs/mark calls.
		tenant_id: typeof o.tenant_id === 'string' ? o.tenant_id : undefined,
	}
}

export function normalizeOrganizationDeviceActionsResponse(
	data: unknown,
	fallbackDeviceId?: string,
): { actions: DeviceActionEntryV3[]; count: number } {
	if (data == null || typeof data !== 'object') {
		return { actions: [], count: 0 }
	}
	const o = data as Record<string, unknown>
	const countRaw = o.count
	const count = typeof countRaw === 'number' ? countRaw : Number(countRaw) || 0
	const raw = o.actions ?? o.result ?? o.data
	const actions = (Array.isArray(raw) ? raw : [])
		.map(item => mapOrganizationDeviceActionToEntry(item, fallbackDeviceId))
		.filter((entry): entry is DeviceActionEntryV3 => entry != null)
	return { actions, count }
}

/**
 * Device-action filters accepted in the body of the mark-as-completed endpoints — the same set the
 * list endpoint takes (`action_id`, `device_id`, `created_by` and `type` are comma-separated lists;
 * dates are RFC3339). The backend struct is `deny_unknown_fields`, so only these keys may be sent —
 * the list-only `tenant` / `reprovision_type` params are rejected.
 */
export interface DeviceActionsCompletionFilters {
	action_id?: string
	device_id?: string
	created_by?: string
	type?: string
	created_at_start?: string
	created_at_end?: string
	updated_at_start?: string
	updated_at_end?: string
}

export interface MarkDeviceActionsAsCompletedOptions {
	/** Explicit pairs. Omit to match on `filters` + `actionStatus` instead. */
	deviceActions?: { action_id: number; device_id: string }[]
	tenantId?: string
	actionStatus?: string
	filters?: DeviceActionsCompletionFilters
}

export interface BulkMarkAsCompletedOptions {
	/** Omit to match on `actionStatus` + `filters` alone. */
	deviceFilter?: DeviceSearchFilters
	tenantId?: string
	actionStatus?: string
	filters?: DeviceActionsCompletionFilters
}

// V3 Platform-level action states
export type ActionState =
	| 'queued'
	| 'in_progress'
	| 'completed'
	| 'failed'
	| 'timed_out_exhausted'
	| 'marked_as_completed'

// V3 Device-reported states (custom states reported by the device)
// These are set by the device firmware and can vary by action type
export type DeviceReportedState =
	| 'ShellSpawned'
	| 'Downloading'
	| 'Downloaded'
	| 'Installing'
	| 'Installed'
	| 'Extracting payload'
	| 'Finalizing update'
	| 'Waiting for user'
	| 'Waiting for Bluetooth'
	| 'PendingApproval'
	| 'Progress'
	| 'Received'
	| string // Allow custom device-reported states

// V3 API Type for triggering an action
export type TriggerActionV3Payload = {
	action_type: string
	payload: Record<string, unknown>
	device_filter: DeviceSearchFilters
	/** Override the action type's timeout. `null` disables the timeout; omit to use the type default. */
	timeout_seconds?: number | null
	/** Override the action state that arms the timeout. Omit to use the type default. */
	timeout_trigger?: 'queued' | 'in_progress'
	/** Override the action type's retry-on-timeout count (0 disables retries). Omit to use the type default. */
	retry_on_timeout_count?: number
}

export type TriggerActionV3Response = {
	id: number
}

// Device action stats entry - contains device_id and progress
export type DeviceActionStatEntry = {
	device_id: string
	progress: number
	errors: string | null // JSON-encoded array of error strings
	/** When this device's action state last changed (device_actions.updated_at). */
	updated_at?: string | null
	/** Org-level reprovision rows only — see DeviceActionEntryV3.filter_device_id. Required by
	 *  /device_actions and .../logs instead of device_id. */
	filter_device_id?: string
	/** Org-level rows only — the device's numeric uid. */
	uid?: string | number
}

// Device action stats - grouped by action_state
// Keys are ActionState values: queued, in_progress, completed, failed, marked_as_completed, timed_out_exhausted
export type DeviceActionStats = Partial<Record<ActionState, DeviceActionStatEntry[]>>

export type ActionV3 = {
	id: number
	type: string
	device_filter: DeviceSearchFilters
	payload: Record<string, unknown>
	device_action_stats: DeviceActionStats
	created_at: string
	created_by: string
}

// V3 single-action response (GET /v3/actions/{id}). Carries the timeout config
// and omits device_action_stats
export type ActionV3WithPayload = Omit<ActionV3, 'device_action_stats'> & {
	/** Override timeout for this action; `null` means the timeout is disabled. */
	timeout_seconds?: number | null
	/** Action state that arms the timeout (backend default `in_progress`). */
	timeout_trigger?: string
	/** Retry-on-timeout count configured for this action. */
	retry_on_timeout_count?: number
	/** Only present for `renew_cert` actions; the reprovision intent. */
	reprovision_type?: 'attach' | 'detach' | 'transfer'
}

/**
 * File artifact metadata merged into `payload` when `include_file_info` is requested — only present
 * for actions whose action type has `payload_type: 'file'` (e.g. `update_firmware`).
 */
export type ActionFileInfo = {
	checksum: string
	content_length: number
	uncompressed_size: number | null
	/** Device-facing artifact URL (device_api_domain) — not browser-fetchable with the session cookie. */
	url: string
}

export type GetActionV3Options = {
	/** Owning project tenant; required at org level where the URL carries no tenant. */
	tenantId?: string
	/** Merges checksum/size/url into `payload` for file-type actions. */
	includeFileInfo?: boolean
	signal?: AbortSignal
}

export type FetchAllActionsV3Response = {
	count: number
	data: ActionV3[]
}

// V3 API Types for Get Devices for Action. device_id here is already the frozen/correct id for
// .../logs and /device_actions — unlike the org-wide list's device_id (see DeviceActionEntryV3).
export type DeviceActionV3 = {
	device_id: string
	/** The device's numeric uid, from the device_action row. Optional — the demo server omits it. */
	uid?: number
	action_state: ActionState
	retry_count: number
	device_reported_state: DeviceReportedState | null
	progress: number | null
	errors: string | null // JSON-encoded array of error strings
}

export type GetDevicesForActionV3Request = {
	page: number
	per_page: number
	/** Exact device id match (backend compares with `=`, no substring). */
	device_id?: string
	device_status?: string
	action_status?: string
}

export type GetDevicesForActionV3Response = {
	count: number
	data: DeviceActionV3[]
}

// V3 API Types for Get Device Action Logs
export type DeviceActionLogV3 = {
	event: string
	timestamp: string
}

/** Paginated response for device action logs. Server wraps rows in `{ count, data }`. */
export type GetDeviceActionLogsV3Response = {
	count: number
	data: DeviceActionLogV3[]
}

/** Pagination + tenant scope for device action logs. Server defaults: page 1, per_page 1000 (max 1000). */
export type GetDeviceActionLogsV3Options = {
	page?: number
	per_page?: number
	tenantId?: string
	signal?: AbortSignal
}

// V3 API Types for /device_actions endpoint (GET)
// Returns device-level action records with full details
export type DeviceActionEntryV3 = {
	action_id: number | string
	device_id: string
	uid?: string | number
	action_state: string
	retry_count: number
	timeout_at: string | null
	updated_at: string | null
	device_reported_state: string | null
	progress: number | null
	errors: string | null // JSON-encoded array of error strings
	mqtt_publish_at: string | null
	payload: Record<string, unknown>
	type: string
	created_at: string
	created_by: string
	/** Owning project tenant of this action (org-level device-actions list only). A device's
	 *  history can span tenants after a transfer, so this is per-action, not the device's tenant. */
	tenant_id?: string
	/**
	 * Org-level reprovision rows only. `device_id` above is a live join to the device's current id;
	 * this is the id frozen at creation time (`device_filter.id.eq`), which is what /device_actions
	 * and .../logs actually filter by — use this field, not `device_id`, for those calls.
	 */
	filter_device_id?: string
	/** Only present for `renew_cert` actions — the reprovision intent (attach/detach/transfer). */
	reprovision_type?: string
}

export type FetchDeviceActionsV3Request = {
	page: number
	per_page: number
	action_state?: string
	action_type?: string
	creator?: string
	/** One or more device ids, comma-separated (IN match). */
	device_id?: string
	/** One or more action ids, comma-separated (IN match on the Command ID column). */
	action_id?: number | string
	/** One or more comma-separated substrings; ORed ILIKE matches on `actions.created_by`. */
	created_by?: string
	/** Substring (ILIKE) match on `actions.type` (Command Name column). */
	type?: string
	/** RFC3339 lower/upper bounds on `actions.created_at`. */
	created_at_start?: string
	created_at_end?: string
	/** RFC3339 lower/upper bounds on `device_actions.updated_at`. */
	updated_at_start?: string
	updated_at_end?: string
}

/** Org Operations adds two filters the project endpoint rejects: owning project, reprovision intent. */
export type FetchOrgDeviceActionsV3Request = FetchDeviceActionsV3Request & {
	action_type?: string
	/** Owning project (tenant id). */
	tenant?: string
	/** Exact match on reprovision intent ('attach' | 'detach' | 'transfer'). */
	reprovision_type?: string
}

export type FetchDeviceActionsV3Response = {
	count: number
	/** Per-action-type totals across all pages, scoped to the request's filters. */
	action_counts?: Record<string, number>
	data: DeviceActionEntryV3[]
}

/** `GET /v3/devices/{device_id}/actions` returns rows and a total only — no per-type counts. */
export type FetchActionsForDeviceResponse = {
	count: number
	data: DeviceActionEntryV3[]
}

export type FetchActionsForDeviceRequest = {
	device_id: string
	page: number
	per_page: number
	/** Comma-separated; matches any of `actions.type` (IN). */
	action_type?: string
	/** Comma-separated; matches any of `actions.created_by` (IN, server name `email`). */
	email?: string
	/** Comma-separated; matches any of `device_actions.action_state` (IN). */
	status?: string
	/** Comma-separated; matches any of `device_actions.action_id` (IN). */
	action_id?: string
}

export type ActionStatusDetail = {
	device_id: number
	status: string
	phase: number
	progress: number
	errors: string[]
	updated_at: Date
}

export interface ActionLog {
	timestamp: number
	status: 'Completed' | 'Queued' | 'Initiated' | 'Failed' | string
	progress: number
	logs: string[]
}

export type LatencyHistogramStats = {
	range_start: number
	range_end: number
	height: number
}[]

export type LatencyHistogramBinV3 = {
	bucket: number
	count: number
	range_start: number // in milliseconds
	range_end: number // in milliseconds
}

export type LatencyHistogramV3Response = {
	count: number
	bins: LatencyHistogramBinV3[]
}

/** device_actions aggregates over the tenant's whole set — tens of seconds on a large fleet, so the 10s default cut it off. */
// Builds the device_actions query params, skipping empty/undefined values. Only the keys
// `/v3/device_actions` accepts — that endpoint is `deny_unknown_fields`, so an org-only key here
// would 400 the whole request. Org extras go through `buildOrgDeviceActionsParams`.
function buildDeviceActionsParams(
	request: FetchDeviceActionsV3Request & { action_type?: string },
): Record<string, string | number> {
	const params: Record<string, string | number> = {
		page: request.page,
		per_page: request.per_page,
	}
	if (request.action_state) {
		params.action_state = request.action_state
	}
	if (request.action_type) {
		params.action_type = request.action_type
	}
	if (request.creator) {
		params.creator = request.creator
	}
	if (request.device_id) {
		params.device_id = request.device_id
	}
	if (request.action_id != null) {
		params.action_id = request.action_id
	}
	if (request.created_by) {
		params.created_by = request.created_by
	}
	if (request.type) {
		params.type = request.type
	}
	if (request.created_at_start) {
		params.created_at_start = request.created_at_start
	}
	if (request.created_at_end) {
		params.created_at_end = request.created_at_end
	}
	if (request.updated_at_start) {
		params.updated_at_start = request.updated_at_start
	}
	if (request.updated_at_end) {
		params.updated_at_end = request.updated_at_end
	}
	return params
}

/** Same params plus the two the org endpoint adds: owning project, and reprovision intent. */
function buildOrgDeviceActionsParams(request: FetchOrgDeviceActionsV3Request): Record<string, string | number> {
	const params = buildDeviceActionsParams(request)
	if (request.tenant) {
		params.tenant = request.tenant
	}
	if (request.reprovision_type) {
		params.reprovision_type = request.reprovision_type
	}
	return params
}

/**
 * Read boundary for the paginated action endpoints. The server always sends `{ count, data }`, but a
 * proxy error page or a stubbed client need not — normalise here so no consumer has to guard and a
 * malformed body degrades to an empty page instead of a TypeError.
 */
function normalizeCountedList<T>(raw: unknown): { count: number; data: T[] } {
	const o = (raw ?? {}) as Record<string, unknown>
	const count = typeof o.count === 'number' ? o.count : Number(o.count) || 0
	return { count, data: Array.isArray(o.data) ? (o.data as T[]) : [] }
}

const v3ActionsApi = {
	fetchAllActionsStatus: async (
		pageNumber: number,
		pageLimit: number,
		sortByIncomplete = false,
		filters: Filters = {},
	): Promise<FetchAllActionsV3Response> => {
		// V3 API uses 'per_page' instead of 'limit'
		const params: Record<string, string | number | boolean> = {
			page: pageNumber,
			per_page: pageLimit,
			incomplete: sortByIncomplete,
		}

		// Map filters to V3 query params
		if (filters.action_type) {
			params.action_type = Array.isArray(filters.action_type)
				? String(filters.action_type[0])
				: String(filters.action_type)
		}
		if (filters.email) {
			params.email = Array.isArray(filters.email) ? String(filters.email[0]) : String(filters.email)
		}

		const queryString = new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)])).toString()

		return normalizeCountedList<ActionV3>(await apiCall(`/v3/actions?${queryString}`))
	},

	/**
	 * Trigger an organization-level action
	 * Reprovision and detach are handled by organizationsApi
	 * Accepts V3-shaped payload: { action_type, device_filter, payload }
	 */
	triggerActionV3: async (
		payload: OrganizationActionPayload,
		options?: { tenantId?: string },
	): Promise<TriggerActionV3Response> => {
		if (payload.action_type === 'detach') {
			const { useOrganizationStore } = await import('@/stores/organizationStore')
			const { useProjectsStore } = await import('@/stores/projectsStore')
			const organizationId = resolveOrganizationId()
			const orgDevices = useOrganizationStore.getState().devices
			const rootProject = useProjectsStore.getState().allProjects.find(p => p.is_org)
			if (!rootProject) {
				throw new Error('No root project found for this organization; detach is not available.')
			}
			for (const d of payload.devices) {
				const od = orgDevices.find(x => x.tenant_id === d.tenant_id && String(x.id) === d.device_id)
				if (od?.uid == null || od.uid === '') {
					throw new Error(`Device ${d.device_id} has no UID; detach requires an org device with UID.`)
				}
				const uid = typeof od.uid === 'number' ? od.uid : Number(od.uid)
				if (!Number.isFinite(uid)) {
					throw new Error(`Device ${d.device_id} has an invalid UID.`)
				}
				await organizationsApi.detachDeviceViaReprovision(organizationId, uid, rootProject.id, payload.params)
			}
			// Detach fans out over organizationsApi, so there is no single action id to report.
			return { id: 0 }
		}
		// Normalize union payload variants to V3 action shape.
		const actionPayload =
			payload.action_type === 'attach'
				? {
						action_type: payload.action_type,
						device_filter: { id: { in: payload.device_ids } },
						payload: payload.params as unknown as Record<string, unknown>,
					}
				: {
						action_type: payload.action_type,
						device_filter: payload.device_filter,
						payload: payload.payload as unknown as Record<string, unknown>,
					}

		// For reprovision if routed here), send V3-shaped payload directly
		return apiCall('/v3/actions', {
			method: 'POST',
			data: actionPayload,
			...(options?.tenantId && {
				headers: {
					'x-bytebeam-tenant': options.tenantId,
					'x-bytebeam-impersonate': 'admin',
				},
			}),
		}) as Promise<TriggerActionV3Response>
	},

	/**
	 * Cross-tenant action list for an org. The endpoint filters on `tenant` and `action_type` only —
	 * its other query params belong to the org device_actions handlers and are ignored here.
	 * Defaults to `renew_cert`, the internal type behind org-level reprovision views.
	 */
	getOrganizationActions: async (
		organizationId: string,
		page = 1,
		perPage = 20,
		tenant?: string,
		actionType = 'renew_cert',
	): Promise<{ actions: OrganizationAction[]; count: number }> => {
		const data = await ownerApiCall<unknown>(`/v3/organizations/${encodeURIComponent(organizationId)}/actions`, {
			method: 'GET',
			params: { page, per_page: perPage, action_type: actionType, ...(tenant ? { tenant } : {}) },
		})
		return normalizeOrganizationActionsResponse(data)
	},

	getDeviceActions: async (
		organizationId: string,
		uid: number,
		page = 1,
		perPage = 20,
		tenant?: string,
		signal?: AbortSignal,
	): Promise<{ actions: DeviceActionEntryV3[]; count: number }> => {
		const data = await ownerApiCall<unknown>(
			`/v3/organizations/${encodeURIComponent(organizationId)}/devices/${encodeURIComponent(String(uid))}/actions`,
			{
				method: 'GET',
				// Org-level views surface only reprovision actions, stored under the internal `renew_cert` type.
				params: { page, per_page: perPage, action_type: 'renew_cert', ...(tenant ? { tenant } : {}) },
				signal,
			},
		)
		return normalizeOrganizationDeviceActionsResponse(data, String(uid))
	},

	// Fetches fleet-wide device-actions for an org. Rows carry tenant_id for the Project column;
	// action_type is passed through (Operations sends renew_cert for reprovision-only).
	fetchOrgDeviceActionsV3: async (
		organizationId: string,
		request: FetchOrgDeviceActionsV3Request,
		signal?: AbortSignal,
	): Promise<{ count: number; action_counts?: Record<string, number>; data: DeviceActionEntryV3[] }> => {
		const data = await ownerApiCall<unknown>(`/v3/organizations/${encodeURIComponent(organizationId)}/device_actions`, {
			method: 'GET',
			params: buildOrgDeviceActionsParams(request),
			signal,
		})
		const o = (data ?? {}) as Record<string, unknown>
		const countRaw = o.count
		const count = typeof countRaw === 'number' ? countRaw : Number(countRaw) || 0
		const action_counts =
			o.action_counts && typeof o.action_counts === 'object' ? (o.action_counts as Record<string, number>) : undefined
		const rawRows = o.data ?? o.result ?? o.actions
		const rows = (Array.isArray(rawRows) ? rawRows : [])
			.map(item => mapOrganizationDeviceActionToEntry(item))
			.filter((entry): entry is DeviceActionEntryV3 => entry != null)
		return { count, action_counts, data: rows }
	},

	/**
	 * Get devices for a specific action with their status (V3)
	 * POST /v3/actions/{action_id}/devices
	 */
	getDevicesForActionV3: async (
		actionId: number,
		request: GetDevicesForActionV3Request,
		tenantId?: string,
		signal?: AbortSignal,
	): Promise<GetDevicesForActionV3Response> =>
		normalizeCountedList<DeviceActionV3>(
			await apiCall(`/v3/actions/${actionId}/devices`, {
				method: 'POST',
				data: request,
				signal,
				...tenantScopedConfig(tenantId),
			}),
		),

	/**
	 * Fetch device-level action records with full details (V3)
	 * GET /v3/device_actions
	 * Returns individual device-action records with status, progress, errors, etc.
	 * Use this for detailed device-level views and filtering by status/type/creator
	 * @param request - Pagination and filter parameters
	 */
	fetchDeviceActionsV3: async (
		request: FetchDeviceActionsV3Request,
		signal?: AbortSignal,
		tenantId?: string,
	): Promise<FetchDeviceActionsV3Response> => {
		const params = buildDeviceActionsParams(request)
		const queryString = new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)])).toString()

		const raw = await apiCall<unknown>(`/v3/device_actions?${queryString}`, {
			signal,
			...tenantScopedConfig(tenantId),
		})
		const rawCounts = (raw as { action_counts?: unknown } | null)?.action_counts
		return {
			...normalizeCountedList<DeviceActionEntryV3>(raw),
			action_counts: rawCounts && typeof rawCounts === 'object' ? (rawCounts as Record<string, number>) : undefined,
		}
	},

	/**
	 * List actions for a single device (V3)
	 * GET /v3/devices/{device_id}/actions
	 * Same response shape as legacy device_actions list; query must only include documented keys.
	 */
	fetchActionsForDevice: async (
		request: FetchActionsForDeviceRequest,
		signal?: AbortSignal,
	): Promise<FetchActionsForDeviceResponse> => {
		const params: Record<string, string | number> = {
			page: request.page,
			per_page: request.per_page,
		}
		if (request.action_type) {
			params.action_type = request.action_type
		}
		if (request.email) {
			params.email = request.email
		}
		if (request.status) {
			params.status = request.status
		}
		if (request.action_id) {
			params.action_id = request.action_id
		}
		const queryString = new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)])).toString()
		return normalizeCountedList<DeviceActionEntryV3>(
			await apiCall(`/v3/devices/${encodeURIComponent(request.device_id)}/actions?${queryString}`, { signal }),
		)
	},

	/**
	 * Get action logs for a specific device (V3)
	 * GET /v3/actions/{action_id}/devices/{device_id}/logs
	 * Paginated: server returns `{ count, data }`; query supports page/per_page (defaults 1/1000).
	 */
	getDeviceActionLogsV3: async (
		actionId: number,
		deviceId: string,
		options?: GetDeviceActionLogsV3Options,
	): Promise<GetDeviceActionLogsV3Response> => {
		const { page, per_page, tenantId, signal } = options ?? {}
		const params: Record<string, number> = {}
		if (page != null) {
			params.page = page
		}
		if (per_page != null) {
			params.per_page = per_page
		}
		const queryString = new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)])).toString()
		const suffix = queryString ? `?${queryString}` : ''
		return normalizeCountedList<DeviceActionLogV3>(
			await apiCall(`/v3/actions/${actionId}/devices/${encodeURIComponent(deviceId)}/logs${suffix}`, {
				signal,
				...tenantScopedConfig(tenantId),
			}),
		)
	},

	/**
	 * Get a single action with its payload (V3)
	 * GET /v3/actions/{action_id}
	 * This replaces the old V1 /actions/{id}/params endpoint
	 */
	getActionV3: async (actionId: number, options?: GetActionV3Options): Promise<ActionV3WithPayload> => {
		const suffix = options?.includeFileInfo ? '?include_file_info=true' : ''
		return apiCall(`/v3/actions/${actionId}${suffix}`, {
			signal: options?.signal,
			...tenantScopedConfig(options?.tenantId),
		})
	},

	/**
	 * Mark action as completed for specific devices (V3)
	 * POST /v3/actions/{action_id}/mark-as-completed
	 * @param actionId - The action ID to mark as completed
	 * @param deviceFilter - Optional device filter to mark specific devices
	 * @param tenantId - Owning project tenant; required at org level where the URL carries no tenant.
	 *   The endpoint is tenant-scoped (RLS), so the header must name the project that owns the action.
	 * @param actionStatus - Narrows to device_actions currently in this state ('queued' | 'in_progress');
	 *   used when a whole status group is selected, so the update matches the group server-side.
	 */
	markActionAsCompletedV3: async (
		actionId: number,
		deviceFilter?: DeviceSearchFilters,
		tenantId?: string,
		actionStatus?: string,
	): Promise<void> =>
		apiCall(`/v3/actions/${actionId}/mark-as-completed`, {
			method: 'POST',
			data: {
				...(deviceFilter ? { device_filter: deviceFilter } : {}),
				...(actionStatus ? { action_status: actionStatus } : {}),
			},
			...tenantScopedConfig(tenantId),
		}),

	/**
	 * Mark active action for a specific device as completed (V3)
	 * POST /v3/devices/{device_id}/mark-as-completed
	 * @param deviceId - The device ID whose active action should be marked as completed
	 * @param tenantId - Owning project tenant; required at org level (see markActionAsCompletedV3).
	 * @param actionStatus - Narrows to device_actions currently in this state (see markActionAsCompletedV3).
	 */
	markDeviceActionAsCompletedV3: async (deviceId: string, tenantId?: string, actionStatus?: string): Promise<void> =>
		apiCall(`/v3/devices/${encodeURIComponent(deviceId)}/mark-as-completed`, {
			method: 'POST',
			params: actionStatus ? { action_status: actionStatus } : undefined,
			...tenantScopedConfig(tenantId),
		}),

	/**
	 * Bulk mark active actions as completed for multiple devices (V3)
	 * POST /v3/actions/mark-as-completed
	 * Used when user selects multiple devices in the same state
	 * @param deviceFilter - Which devices' actions to mark. Omit to match on actionStatus + filters
	 *   alone (permission-scoped) — e.g. an entire status group.
	 * @param tenantId - Owning project tenant; required at org level (see markActionAsCompletedV3).
	 * @param actionStatus - Narrows to device_actions currently in this state (see markActionAsCompletedV3).
	 * @param filters - Same device-action filters the list endpoint takes. Without them a status-only
	 *   call matches every device_action in the tenant, not just the ones the view shows.
	 */
	bulkMarkAsCompletedV3: async ({
		deviceFilter,
		tenantId,
		actionStatus,
		filters,
	}: BulkMarkAsCompletedOptions): Promise<void> =>
		apiCall('/v3/actions/mark-as-completed', {
			method: 'POST',
			data: {
				...(deviceFilter ? { device_filter: deviceFilter } : {}),
				...(actionStatus ? { action_status: actionStatus } : {}),
				...(filters ?? {}),
			},
			...tenantScopedConfig(tenantId),
		}),

	/**
	 * Mark device_actions as completed in a single request (V3)
	 * POST /v3/device-actions/mark-as-completed
	 *
	 * Two forms, both matching only the device_action rows themselves — unlike
	 * `bulkMarkAsCompletedV3`, whose filters select devices and then sweep all their ongoing actions:
	 * - `deviceActions` — explicit (action_id, device_id) pairs, spanning any number of action_ids.
	 * - `filters` + `actionStatus` — every matching row, no ids needed. For a cross-page select-all,
	 *   where enumerating pairs isn't possible.
	 *
	 * One of the two is required; the server rejects a request carrying neither.
	 * @param tenantId - Owning project tenant; required at org level (see markActionAsCompletedV3).
	 * @param actionStatus - Narrows to device_actions currently in this state (see markActionAsCompletedV3).
	 */
	markDeviceActionsAsCompletedV3: async ({
		deviceActions,
		tenantId,
		actionStatus,
		filters,
	}: MarkDeviceActionsAsCompletedOptions): Promise<void> =>
		apiCall('/v3/device-actions/mark-as-completed', {
			method: 'POST',
			data: {
				...(deviceActions ? { device_actions: deviceActions } : {}),
				...(actionStatus ? { action_status: actionStatus } : {}),
				...(filters ?? {}),
			},
			...tenantScopedConfig(tenantId),
		}),

	/**
	 * Get latency histogram for an action (V3)
	 * GET /v3/actions/latency-histogram?action_id={id}&action_state={state}&bins={count}
	 * @param actionId - The action ID to get latency histogram for
	 * @param actionState - The action state to filter by (e.g., 'in_progress', 'completed')
	 * @param bins - Number of histogram bins, 1-100 (server default 20 when omitted)
	 */
	getLatencyHistogramV3: async (
		actionId: number,
		actionState: string,
		bins = 25,
		tenantId?: string,
	): Promise<LatencyHistogramV3Response> => {
		const params = new URLSearchParams({
			action_id: String(actionId),
			action_state: actionState,
			bins: String(bins),
		})
		const raw = await apiCall<unknown>(`/v3/actions/latency-histogram?${params.toString()}`, {
			...tenantScopedConfig(tenantId),
		})
		// Same reasoning as normalizeCountedList; this endpoint keys its rows `bins`, not `data`.
		const o = (raw ?? {}) as Record<string, unknown>
		return {
			count: typeof o.count === 'number' ? o.count : Number(o.count) || 0,
			bins: Array.isArray(o.bins) ? (o.bins as LatencyHistogramBinV3[]) : [],
		}
	},

	/**
	 * Create/Trigger an action (V3)
	 * POST /v3/actions
	 * @param payload - The action payload containing action_type, payload, and device_filter
	 * @param tenantId - Optional tenant ID for impersonation (cross-tenant action creation)
	 * @returns The created action ID
	 */
	createActionV3: async (payload: TriggerActionV3Payload, tenantId?: string): Promise<TriggerActionV3Response> => {
		const options: AxiosRequestConfig = {
			method: 'POST',
			data: payload,
			...(tenantId && {
				headers: {
					'x-bytebeam-tenant': tenantId,
					'x-bytebeam-impersonate': 'admin',
				},
			}),
		}
		return apiCall('/v3/actions', options)
	},
}

export const actionsApi = isDemoDeployment() ? demoActionsApi : v3ActionsApi
